import { noop } from 'lodash-es';
import { FormattedMessage, useIntl } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Text } from '~/eds/src/components/Text/Text';
import { EntityType } from '~/enums/EntityType';
import { QueryFilters } from '~/features/advanced-search/query-filters/QueryFilters';
import { PromptModelConfig } from '~/redux/api/types/sylvanus/model';

interface Props {
  config: PromptModelConfig;
  entityType: EntityType.DocumentAttachedData | EntityType.Field;
  maxW?: string;
}

export const ConfigurationDetails = ({ config, entityType, maxW }: Props) => {
  const intl = useIntl();

  const entityTypeDisplay =
    entityType === EntityType.DocumentAttachedData
      ? intl.formatMessage({
          id: 'CustomAiDebugModel.tableLabel',
          defaultMessage: 'Table',
          description: 'Label for the table entity type',
        })
      : intl.formatMessage({
          id: 'CustomAiDebugModel.fieldLabel',
          defaultMessage: 'Field',
          description: 'Label for the field entity type',
        });

  return (
    <Layout direction="column" flex="1 0 auto" spacing={4} maxW={maxW}>
      <Text variant="section">
        <FormattedMessage
          id="CustomAiDebugModel.modelConfiguration"
          defaultMessage="Model Configuration"
          description="Label for the model configuration details section header"
        />
      </Text>
      <Layout direction="column" spacing={1}>
        <Text variant="body-bold">
          <FormattedMessage
            id="CustomAiDebugModel.configurationFieldName"
            defaultMessage="{entityTypeDisplay} Name"
            description="Label for the field name of the model"
            values={{ entityTypeDisplay }}
          />
        </Text>
        <Text>{config.field_name}</Text>
      </Layout>
      <Layout direction="column" spacing={1}>
        <Text variant="body-bold">
          <FormattedMessage
            id="CustomAiDebugModel.configurationInstructions"
            defaultMessage="Instructions"
            description="Label for the model instructions"
          />
        </Text>
        <Markdown text={config.instructions} />
      </Layout>
      <Layout direction="column" spacing={1}>
        <Text variant="body-bold">
          <FormattedMessage
            id="CustomAiDebugModel.configurationScope"
            defaultMessage="Scope"
            description="Label for the model scope"
          />
        </Text>
        <QueryFilters
          readOnly
          mode="preview"
          placeholder="—"
          query={config.scope?.data?.attributes?.query ?? []}
          onQueryChange={noop}
        />
      </Layout>
    </Layout>
  );
};
